import { useState, useCallback, useRef } from 'react'
import {
  Box,
  HStack,
  VStack,
  Text,
  IconButton,
  Button,
  ButtonGroup,
  Icon,
  Spinner,
  Badge,
  Dialog,
  Portal,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { FiChevronLeft, FiChevronRight, FiCalendar, FiSave, FiList, FiGrid } from 'react-icons/fi'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensors,
  useSensor,
  DragOverlay,
} from '@dnd-kit/core'
import type { DragStartEvent, DragEndEvent, CollisionDetection } from '@dnd-kit/core'
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable'
import { useItineraryEditor } from '../../../hooks/useItineraryEditor'
import { useIsMobile } from '../../../hooks/useIsMobile'
import type {
  ItineraryAssignmentDto,
  ItineraryTurfPoolDto,
} from '../../../types/campaign'
import { getTurfColor } from '../../../lib/mapConfig'
import { ItineraryWeekGrid } from './ItineraryWeekGrid'
import { ItineraryTurfPool } from './ItineraryTurfPool'
import { StaticAssignmentGrid } from './StaticAssignmentGrid'
import { ItineraryDayAgenda } from './ItineraryDayAgenda'
import { StickyActionFooter } from '../../shared'

function formatWeekRange(weekStart: string): string {
  const start = new Date(weekStart + 'T00:00:00')
  const end = new Date(start)
  end.setDate(end.getDate() + 6)
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  return `${months[start.getMonth()]} ${start.getDate()} — ${months[end.getMonth()]} ${end.getDate()}, ${end.getFullYear()}`
}

interface ItineraryBuilderProps {
  campaignId: string
  /** Renders a non-interactive (no drag-and-drop, no editing) view. Default false. */
  readOnly?: boolean
}

export function ItineraryBuilder({ campaignId, readOnly = false }: ItineraryBuilderProps) {
  const cancelRef = useRef<HTMLButtonElement>(null)
  const gridRef = useRef<HTMLDivElement>(null)

  // All editor state now lives in the shared hook
  const editor = useItineraryEditor(campaignId)
  const {
    weekStart, isStatic, canvassers: localCanvassers, poolTurfs, scheduleMap,
    isLoading, error, isDirty, isSaving, isTogglingMode,
    assignTurf, moveAssignment, removeAssignment, toggleAvailability,
    save, goToPreviousWeek, goToNextWeek, goToToday,
    pendingWeekNav, confirmWeekNav, cancelWeekNav, toggleMode,
  } = editor
  const isMobile = useIsMobile()

  // Drag overlay state — DnD-only, stays local to the desktop view
  const [activeDrag, setActiveDrag] = useState<(ItineraryAssignmentDto | ItineraryTurfPoolDto) & { _dragType: 'assignment' | 'pool-turf' } | null>(null)

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  // Custom collision detection: only match drop zones inside the assignment grid.
  // Drops outside the grid (e.g. back onto the turf pool or empty space) return no
  // collisions so the drag is cancelled instead of snapping to the nearest row.
  const gridAwareCollision: CollisionDetection = useCallback((args) => {
    if (gridRef.current && args.pointerCoordinates) {
      const rect = gridRef.current.getBoundingClientRect()
      const { x, y } = args.pointerCoordinates
      if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
        return []
      }
    }
    return closestCenter(args)
  }, [])

  // Handle drag start
  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event
    const data = active.data.current

    if (data?.type === 'assignment') {
      setActiveDrag({ ...(data.assignment as ItineraryAssignmentDto), _dragType: 'assignment' })
    } else if (data?.type === 'pool-turf') {
      setActiveDrag({ ...(data.turf as ItineraryTurfPoolDto), _dragType: 'pool-turf' })
    }
  }

  // Handle drag end — resolve the DnD target cell, then delegate the mutation to the hook
  const handleDragEnd = useCallback((event: DragEndEvent) => {
    setActiveDrag(null)
    const { active, over } = event
    if (!over) return

    const activeData = active.data.current
    const overData = over.data.current

    let targetGrantId: string | undefined
    let targetDateStr: string | undefined
    if (overData?.type === 'cell') {
      targetGrantId = overData.grantId as string
      targetDateStr = overData.dateStr as string
    } else if (overData?.type === 'assignment') {
      const a = overData.assignment as ItineraryAssignmentDto
      for (const c of localCanvassers) {
        for (const [dateStr, arr] of Object.entries(c.dayAssignments)) {
          if (arr.some(x => x.id === a.id)) { targetGrantId = c.grantId; targetDateStr = dateStr }
        }
      }
    }
    if (!targetGrantId || !targetDateStr) return

    if (activeData?.type === 'pool-turf') {
      assignTurf(activeData.turf as ItineraryTurfPoolDto, targetGrantId, targetDateStr)
    } else if (activeData?.type === 'assignment') {
      moveAssignment(activeData.assignment as ItineraryAssignmentDto, targetGrantId, targetDateStr)
    }
  }, [localCanvassers, assignTurf, moveAssignment])

  // Get the drag overlay turfIndex for color
  const dragTurfIndex = activeDrag
    ? ('turfIndex' in activeDrag ? activeDrag.turfIndex : 0)
    : 0

  if (isLoading) {
    return (
      <VStack py={8}>
        <Spinner size="lg" color="accent" />
        <Text color="gray.500" fontSize="sm">Loading assignments...</Text>
      </VStack>
    )
  }

  if (error) {
    return (
      <Box p={4} bg="red.50" borderRadius="md">
        <Text color="red.600" fontSize="sm">Failed to load assignments. Please try again.</Text>
      </Box>
    )
  }

  if (isMobile) {
    return (
      <>
        <ItineraryDayAgenda editor={editor} />
        {isDirty && (
          <StickyActionFooter>
            <Button
              colorPalette="brand"
              loading={isSaving}
              onClick={() => save({
                onSuccess: () => toaster.create({ title: isStatic ? 'Assignments saved' : 'Itinerary saved', type: 'success', duration: 2000 }),
                onError: () => toaster.create({ title: 'Failed to save', type: 'error', duration: 3000 }),
              })}
            >
              Save changes
            </Button>
          </StickyActionFooter>
        )}
        <Dialog.Root
          open={pendingWeekNav !== null}
          initialFocusEl={() => cancelRef.current}
          role='alertdialog'
          onOpenChange={e => {
            if (!e.open) {
              cancelWeekNav();
            }
          }}>
          <Portal>

            <Dialog.Backdrop>
              <Dialog.Positioner>
                <Dialog.Content>
                  <Dialog.Header fontSize="lg" fontWeight="bold">Unsaved Changes</Dialog.Header>
                  <Dialog.Body>You have unsaved changes to this week. Navigating away will discard them.</Dialog.Body>
                  <Dialog.Footer>
                    <Button ref={cancelRef} onClick={cancelWeekNav}>Cancel</Button>
                    <Button colorPalette="red" onClick={confirmWeekNav} ml={3}>Discard Changes</Button>
                  </Dialog.Footer>
                </Dialog.Content>
              </Dialog.Positioner>
            </Dialog.Backdrop>

          </Portal>
</Dialog.Root>
      </>
    );
  }

  return (
    <Box>
      {/* Header: Mode toggle + Week navigation (itinerary only) + Save button */}
      <HStack justify="space-between" mb={4} flexWrap="wrap" gap={2}>
        <HStack gap={3}>
          {/* Mode toggle — hidden in read-only mode (switching modes is an edit action) */}
          {readOnly ? (
            <Badge colorPalette={isStatic ? 'blue' : 'purple'} variant="subtle">
              {isStatic ? 'Static' : 'Itinerary'}
            </Badge>
          ) : (
            <ButtonGroup size="xs" attached variant="outline">
              <Button
                colorPalette={isStatic ? 'blue' : undefined}
                variant={isStatic ? 'solid' : 'outline'}
                onClick={() => toggleMode('Static', {
                  onSuccess: () => toaster.create({ title: 'Switched to static assignments', type: 'success', duration: 2000 }),
                  onError: () => toaster.create({ title: 'Failed to switch mode', type: 'error', duration: 3000 }),
                  onBlockedDirty: () => toaster.create({ title: 'Save your changes before switching modes', type: 'warning', duration: 3000 }),
                })}
                loading={isTogglingMode && isStatic}><FiList />Static
                              </Button>
              <Button
                colorPalette={!isStatic ? 'blue' : undefined}
                variant={!isStatic ? 'solid' : 'outline'}
                onClick={() => toggleMode('Itinerary', {
                  onSuccess: () => toaster.create({ title: 'Switched to itinerary mode', type: 'success', duration: 2000 }),
                  onError: () => toaster.create({ title: 'Failed to switch mode', type: 'error', duration: 3000 }),
                  onBlockedDirty: () => toaster.create({ title: 'Save your changes before switching modes', type: 'warning', duration: 3000 }),
                })}
                loading={isTogglingMode && !isStatic}><FiGrid />Itinerary
                              </Button>
            </ButtonGroup>
          )}

          {/* Week navigation — itinerary mode only */}
          {!isStatic && (
            <HStack gap={1}>
              <Icon color="gray.500" asChild><FiCalendar /></Icon>
              <IconButton
                aria-label="Previous week"
                size="sm"
                variant="ghost"
                onClick={goToPreviousWeek}><FiChevronLeft /></IconButton>
              <Text fontWeight="semibold" fontSize="sm" minW="200px" textAlign="center">
                {formatWeekRange(weekStart)}
              </Text>
              <IconButton aria-label="Next week" size="sm" variant="ghost" onClick={goToNextWeek}><FiChevronRight /></IconButton>
              <Button size="xs" variant="outline" onClick={goToToday}>
                Today
              </Button>
            </HStack>
          )}

          {isStatic && (
            <Text fontSize="xs" color="gray.500">
              Standing turf assignments (no daily scheduling)
            </Text>
          )}
        </HStack>

        {!readOnly && (
          <HStack gap={2}>
            {isDirty && (
              <Badge colorPalette="orange" variant="subtle">
                Unsaved changes
              </Badge>
            )}
            <Button
              size="sm"
              colorPalette="brand"
              onClick={() => save({
                onSuccess: () => toaster.create({ title: isStatic ? 'Assignments saved' : 'Itinerary saved', type: 'success', duration: 2000 }),
                onError: () => toaster.create({ title: 'Failed to save', type: 'error', duration: 3000 }),
              })}
              loading={isSaving}
              disabled={!isDirty}><FiSave />Save
                          </Button>
          </HStack>
        )}
      </HStack>

      {/* Main content: Grid (+ Turf Pool sidebar and drag-and-drop when editable) */}
      {readOnly ? (
        <Box>
          {isStatic ? (
            <StaticAssignmentGrid canvassers={localCanvassers} />
          ) : (
            <ItineraryWeekGrid
              weekStart={weekStart}
              canvassers={localCanvassers}
              scheduleMap={scheduleMap}
            />
          )}
        </Box>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={gridAwareCollision}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <HStack align="start" gap={3}>
            <Box ref={gridRef} flex={1}>
              {isStatic ? (
                <StaticAssignmentGrid
                  canvassers={localCanvassers}
                  onRemove={removeAssignment}
                />
              ) : (
                <ItineraryWeekGrid
                  weekStart={weekStart}
                  canvassers={localCanvassers}
                  scheduleMap={scheduleMap}
                  onRemove={removeAssignment}
                  onToggleAvailability={toggleAvailability}
                />
              )}
            </Box>
            <ItineraryTurfPool turfs={poolTurfs} />
          </HStack>
          <DragOverlay>
            {activeDrag && (
              <Box
                bg="surface"
                border="2px solid"
                borderColor={getTurfColor(dragTurfIndex)}
                borderLeft="4px solid"
                borderLeftColor={getTurfColor(dragTurfIndex)}
                borderRadius="md"
                p={2}
                shadow="lg"
                fontSize="xs"
                maxW="160px"
              >
                <HStack gap={1}>
                  <Box w="8px" h="8px" borderRadius="sm" bg={getTurfColor(dragTurfIndex)} flexShrink={0} />
                  <Text fontWeight="semibold" lineClamp={1}>
                    {'turfLabel' in activeDrag ? activeDrag.turfLabel : ''}
                  </Text>
                </HStack>
                <Text color="gray.500">
                  {'turfMapLabel' in activeDrag ? activeDrag.turfMapLabel : ''}
                </Text>
              </Box>
            )}
          </DragOverlay>
        </DndContext>
      )}

      {/* Week navigation unsaved changes dialog */}
      <Dialog.Root
        open={pendingWeekNav !== null}
        initialFocusEl={() => cancelRef.current}
        role='alertdialog'
        onOpenChange={e => {
          if (!e.open) {
            cancelWeekNav();
          }
        }}>
        <Portal>

          <Dialog.Backdrop>
            <Dialog.Positioner>
              <Dialog.Content>
                <Dialog.Header fontSize="lg" fontWeight="bold">
                  Unsaved Changes
                </Dialog.Header>
                <Dialog.Body>
                  You have unsaved changes to this week's itinerary. Navigating away will discard them.
                </Dialog.Body>
                <Dialog.Footer>
                  <Button ref={cancelRef} onClick={cancelWeekNav}>
                    Cancel
                  </Button>
                  <Button colorPalette="red" onClick={confirmWeekNav} ml={3}>
                    Discard Changes
                  </Button>
                </Dialog.Footer>
              </Dialog.Content>
            </Dialog.Positioner>
          </Dialog.Backdrop>

        </Portal>
</Dialog.Root>

    </Box>
  );
}
